import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { FormErrorBanner } from '../FormErrorBanner';
import { hiringLinksAPI, type HiringCancelResult, type HiringLink } from '../../services/api';
import {
  CANCEL_WORDS,
  RAISE_CHECK_AFTER_MS,
  cancelErrorText,
  cancelStandingSentence,
  formatHiringMoment,
  isHiringOff,
  localiseRaiseMessage,
} from './hiringText';

/**
 * A cancel that Recruitment has not confirmed: where it stands, and the one thing that
 * can be done about it. Shown in the hiring panel for a link that is still followed and
 * whose cancel is open - straight after a cancel that was not confirmed, and for a link
 * that arrives from the list already saying so. Also for a link a person stopped
 * following while its cancel could not be settled (Recruitment's list could not be read
 * at that moment): its closed card is where that cancel is still checked from.
 *
 * NOTHING HERE SENDS ANYTHING TO RECRUITMENT. "Check again" reads Recruitment's own list
 * of the budget's openings: gone from it, the opening was deleted and the link is closed;
 * still in it after the settle time, it was not. There is no "Cancel again" here, on
 * purpose: the cancel may already have deleted the opening, and only that list can say.
 * Nobody settles a cancel by hand either.
 *
 * For someone who may manage hiring it checks again by itself a few times (5, 15 and 45
 * seconds after it is shown), then leaves a "Check again" button. Someone who may only
 * view sees the sentence and nothing to press.
 */

type Answer = Pick<HiringCancelResult, 'outcome' | 'message'>;

const SMALL_BUTTON = 'rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50';

export function CancelStatus({
  projectId, link, canManage, initial = null, onResult, onHiringGone,
}: {
  projectId: number;
  /** The link whose opening is being cancelled, as its owner has it now. */
  link: HiringLink;
  canManage: boolean;
  /** The answer the cancel was last given, when its owner has one (the cancel itself, just answered). */
  initial?: Answer | null;
  /** A check was answered: the cancel as the server has it now. */
  onResult: (result: HiringCancelResult) => void;
  /** Hiring was switched off for the project while this was on show. */
  onHiringGone: () => void;
}) {
  const [answer, setAnswer] = useState<Answer | null>(initial);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The checks ChannelPlex makes by itself are over: from then on the button is offered.
  const [autoDone, setAutoDone] = useState(false);

  const alive = useRef(true);
  // One call at a time, whatever asks for it: a timer, a click, a second click.
  const working = useRef(false);
  const latest = useRef({ onResult, onHiringGone });
  latest.current = { onResult, onHiringGone };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const check = async (by: 'itself' | 'person') => {
    if (working.current) return;
    working.current = true;
    setChecking(true);
    if (by === 'person') setError(null);
    try {
      const res = await hiringLinksAPI.checkCancel(projectId, link.id);
      const result = res?.data?.data;
      if (result) {
        // The owner is told whether or not this is still on show.
        latest.current.onResult(result);
        if (alive.current) setAnswer({ outcome: result.outcome, message: result.message });
      }
    } catch (err) {
      if (isHiringOff(err)) latest.current.onHiringGone();
      // A check made by itself that fails says nothing: the next one, or the button, asks again.
      else if (alive.current && by === 'person') setError(cancelErrorText(err, CANCEL_WORDS.checkFailed));
    } finally {
      working.current = false;
      if (alive.current) setChecking(false);
    }
  };
  const checkNow = useRef(check);
  checkNow.current = check;

  useEffect(() => {
    if (!canManage) return undefined;
    setAutoDone(false);
    const last = RAISE_CHECK_AFTER_MS.length - 1;
    const timers = RAISE_CHECK_AFTER_MS.map((ms, i) => setTimeout(() => {
      if (i === last) setAutoDone(true);
      void checkNow.current('itself');
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [canManage, projectId, link.id]);

  const message = localiseRaiseMessage(answer?.message) || cancelStandingSentence(link);
  const sentAt = link.cancel?.sentAt ?? null;

  return (
    <div role="group" aria-label={CANCEL_WORDS.standingLabel} className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
      <p className="text-sm text-gray-900">{message}</p>
      {sentAt && <p className="text-xs text-gray-600">{CANCEL_WORDS.sentOn(formatHiringMoment(sentAt))}</p>}
      {error && <FormErrorBanner message={error} />}
      {canManage && (!autoDone ? (
        <p className="text-xs text-gray-600">{CANCEL_WORDS.checksByItself}</p>
      ) : (
        <button
          type="button"
          onClick={() => void check('person')}
          disabled={checking}
          className={`${SMALL_BUTTON} inline-flex items-center gap-1 border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
        >
          {checking && <Loader2 className="h-3 w-3 animate-spin" />}
          {checking ? CANCEL_WORDS.checking : CANCEL_WORDS.checkAgain}
        </button>
      ))}
    </div>
  );
}

export default CancelStatus;
